import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import type { FormControl, NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { I18nLanguage } from '@onyx/angular';
import {
  FormHelper,
  I18N_LANGUAGES,
  OnyxButtonComponent,
  OnyxFormGroupComponent,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxTextFieldComponent,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import type { SetNonNullable } from 'type-fest';
import { DictionaryCode } from '../../../../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../../../../common/helpers/validation.helper';
import { LanguageService } from '../../../../../../../common/services/language.service';
import { CompanyDictionariesService } from '../../../services/company-dictionaries.service';

type DictionariesNamesTranslationFormGroup = ReturnType<
  typeof DictionariesNamesTranslationFormComponent.buildFom
>;

export type DictionariesNamesTranslation = SetNonNullable<
  ReturnType<DictionariesNamesTranslationFormGroup['getRawValue']>
>;

@Component({
  selector: 'app-dictionaries-names-translation-form',
  imports: [
    ReactiveFormsModule,
    OnyxFormGroupComponent,
    OnyxTextFieldComponent,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxIconButtonComponent,
    OnyxIconComponent,
  ],
  templateUrl: './dictionaries-names-translation-form.component.html',
  styleUrl: './dictionaries-names-translation-form.component.scss',
})
export class DictionariesNamesTranslationFormComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly injector = inject(Injector);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );

  public readonly form =
    input.required<DictionariesNamesTranslationFormGroup>();
  public readonly label = input.required<string>();
  public readonly initialValue = input<string>();

  protected readonly I18N = 'dictionaries';

  protected readonly DictionaryCode = DictionaryCode;

  protected language = signal(this.languageService.getLanguage());
  protected languages = computed(() =>
    I18N_LANGUAGES.filter((language) => language !== this.language()),
  );
  protected showTranslations = signal(false);
  protected loading = signal(false);

  public ngOnInit(): void {
    const initialValue = this.initialValue();
    if (this.initialValue()) {
      this.form().patchValue({ [this.language()]: initialValue });
    }

    effect(
      () => {
        for (const language of I18N_LANGUAGES) {
          const control = this.form().controls[language];
          if (language === this.language()) {
            control.addValidators([Validators.required]);
          } else {
            control.removeValidators([Validators.required]);
          }
          control.updateValueAndValidity();
        }
      },
      { injector: this.injector },
    );
  }

  protected toggleTranslations(): void {
    this.showTranslations.update((showTranslations) => !showTranslations);
  }

  protected async suggestTranslations(): Promise<void> {
    const requiredControl = this.form().controls[this.language()];
    if (
      !(await ValidationHelper.checkValidity(requiredControl, this.injector))
    ) {
      return;
    }

    this.loading.set(true);
    this.companyDictionariesService
      .suggestTranslations(this.language(), requiredControl.value!)
      .subscribe((translations) => this.form().setValue(translations))
      .add(() => this.loading.set(false));
  }

  protected clearTranslations(): void {
    for (const language of this.languages()) {
      FormHelper.reset(this.form().controls[language]);
    }
  }

  public static buildFom(fb: NonNullableFormBuilder) {
    return fb.group(
      Object.fromEntries(
        I18N_LANGUAGES.map((language) => [
          language,
          fb.control<string | null>(null, [onyxTextFieldLengthValidator]),
        ]),
      ) as Record<I18nLanguage, FormControl<string | null>>,
    );
  }
}
